import { makeAutoObservable, runInAction } from 'mobx'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { type ITabItem, PhoneInputStore, showToast } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { submitDataToHubspotForm } from 'shared/lib'
import { getEnvVar } from 'shared/config/lib/getEnvVar'
import { LINK_MEETINGS_HUBSPOT_1, LINK_MEETINGS_HUBSPOT_2 } from 'shared/constants/links'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { type BillingPlan } from 'entities/Billing/model/BillingPlan'
import { getIntervalMonthCount } from 'entities/Billing/lib/billingInterval'
import { type BillingInterval } from 'entities/Billing/api/types'
import { openIntercom } from 'entities/Intercom'
import { BillingPath } from 'entities/ExportRouters'
import { type BillingPeriod } from 'pages/settings/ui/BillingPlan'
import {
  ADDITIONAL_CREDIT_PRECISION,
  CUSTOM_PLAN_PRICE_DIVISOR,
  DEFAULT_PERIOD_TEXT,
  PERIOD_PRICE_LABELS,
} from 'pages/settings/pages/billing/pages/overview/ui/OpportunityModal/constants/constants'
import {
  GetCallModalActions,
  GetCallModalContent,
} from 'pages/settings/pages/billing/pages/upgrade/ui/GetCallModal'
import { type CurrentPlanData, type PlanPeriod, type RecommendedPlanData } from './types'

export type ConvertedCurrentPlanCredits = {
  totalCredits: number
  planCredits: number
  planPeriod: PlanPeriod
  planPrice: number
  overageCredits: number
  overageCost: number
}

export class OpportunityModalStore {
  private _loadingGetCall = false
  private _phoneInputStore
  private _selectedDate: Date | null = null
  private _period: BillingPeriod = 'annually'

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)

    this._phoneInputStore = new PhoneInputStore({
      number: this.userProfile?.number,
    })
    this._selectedDate = uiStore.dayjs().add(2, 'hours').toDate()
    this.sendOpportunityEvent('Offer Viewed')
  }

  private getPeriodText = (period?: string | null): string => {
    if (!period) return DEFAULT_PERIOD_TEXT
    return PERIOD_PRICE_LABELS[period] || DEFAULT_PERIOD_TEXT
  }

  private calculatePlanPrice = (price: number, isCustomPlan: boolean): number => {
    if (isCustomPlan) {
      return Math.round(price) / CUSTOM_PLAN_PRICE_DIVISOR
    }
    return Math.round(price)
  }

  private calculateAdditionalCreditPrice = (
    additionalCreditAmount: number,
    isCustomPlan: boolean
  ): number => {
    if (isCustomPlan) {
      return Math.round(additionalCreditAmount * ADDITIONAL_CREDIT_PRECISION)
    }
    return additionalCreditAmount
  }

  private calculateTotalCost = (planPrice: number, overageCost: number): number => {
    const roundedPlanPrice = Math.round(planPrice)
    const ceilOverageCost = Math.ceil(overageCost)
    return Math.ceil(roundedPlanPrice + ceilOverageCost)
  }

  closeGetCallModal = () => {
    modalStore.removeModal('getCallModal')
  }

  showGetCallModal = () => {
    modalStore.addModal({
      id: 'getCallModal',
      title: 'Get a call',
      showCloseButton: false,
      disabledOverlayClose: true,
      onClose: () => {
        this.closeGetCallModal()
      },
      width: '480px',
      paddingContent: '8px 24px 12px 24px',
      ModalContent: GetCallModalContent,
      ModalActions: GetCallModalActions,
      ModalContentProps: {
        store: this,
      },
    })
  }

  get currentPlanData(): CurrentPlanData {
    const planCredits = this.subscriptionPlanCredits || 0
    const subscriptionSuggestion = this.subscriptionSuggestion
    const planPeriod = subscriptionSuggestion?.current_plan_period === 'yearly' ? 'year' : 'month'
    const overageCredits = subscriptionSuggestion?.bought_credits_amount || 0
    const overageCost = subscriptionSuggestion?.bought_credits_cost || 0
    const isCustomPlan = this.subscriptionPlan?.custom || false

    const roundedPlanPrice = this.calculatePlanPrice(
      this.subscriptionPlan?.price || 0,
      isCustomPlan
    )
    const additionalCreditPrice = this.calculateAdditionalCreditPrice(
      this.subscriptionPlan?.additional_credit_amount || 0,
      isCustomPlan
    )
    const totalCost = this.calculateTotalCost(roundedPlanPrice, overageCost)

    return {
      planCredits,
      planPeriod,
      overageCredits,
      overageCost,
      planPrice: roundedPlanPrice,
      additionalCreditPrice,
      totalCost,
      isCustomPlan,
    }
  }

  get showAdditionalPriceInfo(): boolean {
    return (
      this.currentPlanData.planPeriod === 'month' &&
      this.suggestedPlanPeriod === 'year' &&
      !this.showCustomPlanUI
    )
  }

  get recommendedPlanData(): RecommendedPlanData | null {
    if (this.showCustomPlanUI) return null

    const credits = this.showAdditionalPriceInfo
      ? this.suggestedYearlyPlanCredits
      : this.suggestedPlanCredits
    const extraCredits = this.showAdditionalPriceInfo
      ? this.suggestedYearlyPlanExtraCredits
      : this.suggestedPlanExtraCredits
    const extraPrice = this.showAdditionalPriceInfo
      ? this.suggestedYearlyPlanExtraPrice
      : this.suggestedPlanExtraPrice

    return {
      credits,
      extraCredits,
      extraPrice,
      totalPrice: this.suggestedPlanTotalPrice,
      period: this.suggestedPlanPeriod,
      additionalCreditPrice: this.suggestedPlanAdditionalCreditPrice,
      additionalCreditPriceDifference: this.additionalCreditPriceDifference,
      saveCost: this.upgradeSaveCost,
    }
  }

  get periodText(): string {
    return this.getPeriodText(this.subscriptionSuggestion?.current_plan_period)
  }

  get showTimePeriodTabs(): boolean {
    return (
      !this.currentPlanData.isCustomPlan &&
      this.currentPlanData.planPeriod === 'month' &&
      !this.showCustomPlanUI
    )
  }

  get period(): BillingPeriod {
    return this._period
  }

  setPeriod = (period: BillingPeriod) => {
    this._period = period
  }

  handleSetPeriod = (tab: ITabItem<BillingPeriod>) => {
    this._period = tab.key
  }

  get showGreyBorder(): boolean {
    return this.showTimePeriodTabs && this._period === 'monthly'
  }

  get subscriptionSuggestion() {
    return this._entities.subscriptionStore.subscriptionSuggestion
  }

  get subscriptionPlan() {
    return this._entities.subscriptionStore.plan
  }

  get showCustomPlanUI() {
    return (
      this.subscriptionPlan?.custom ||
      (this.subscriptionSuggestion?.suggested_plan === null &&
        this.subscriptionSuggestion?.suggested_annual_plan === null)
    )
  }

  private get isCurrentPlanNotYearlyAndPeriodYearly() {
    const subscriptionSuggestion = this.subscriptionSuggestion
    if (!subscriptionSuggestion) return false

    const isCurrentPlanNotYearly = subscriptionSuggestion.current_plan_period !== 'yearly'
    const isPeriodYearly = this._period === 'annually'

    return isCurrentPlanNotYearly && isPeriodYearly
  }

  private get subscriptionPlanCredits() {
    return this._entities.subscriptionStore.planCredits
  }

  private get baseCreditsUsed() {
    const subscriptionSuggestion = this.subscriptionSuggestion
    if (!subscriptionSuggestion) return 0

    const boughtCreditsAmount = subscriptionSuggestion.bought_credits_amount || 0
    const planCredits = this.subscriptionPlanCredits || 0

    return boughtCreditsAmount + planCredits
  }

  private calculateSuggestedPlanTotalPrice = (
    plan: BillingPlan | null | undefined,
    creditsMultiplier = 1
  ): number => {
    if (!plan) return 0

    const totalCreditsUsed = this.baseCreditsUsed * creditsMultiplier
    const extraCredits = Math.max(0, totalCreditsUsed - plan.credits)
    const additionalCreditAmount = plan.origin.additional_credit_amount || 0
    const extraPrice = Math.ceil(extraCredits * additionalCreditAmount)
    const basePrice = plan.price_dollars || 0

    return Math.ceil(basePrice + extraPrice)
  }

  private get activeYearlySuggestedPlan() {
    const subscriptionSuggestion = this.subscriptionSuggestion
    if (!subscriptionSuggestion) return null

    const suggestedPlanId = subscriptionSuggestion.suggested_annual_plan
    if (!suggestedPlanId) return null

    return this._entities.billingStore.billingPlansStore.getItem(suggestedPlanId) || null
  }

  get suggestedMonthlyTotalPriceYearly() {
    if (!this.isCurrentPlanNotYearlyAndPeriodYearly) return 0

    const suggestedMonthlyPlanId = this.subscriptionSuggestion?.suggested_plan
    if (!suggestedMonthlyPlanId) return 0

    const suggestedMonthlyPlan =
      this._entities.billingStore.billingPlansStore.getItem(suggestedMonthlyPlanId)

    return this.calculateSuggestedPlanTotalPrice(suggestedMonthlyPlan) * 12
  }

  get suggestedYearlyPlanTotalPrice() {
    if (!this.isCurrentPlanNotYearlyAndPeriodYearly) return 0

    return this.calculateSuggestedPlanTotalPrice(this.activeYearlySuggestedPlan, 12)
  }

  get suggestedPlanPeriod() {
    return this._period === 'annually' ? 'year' : 'month'
  }

  private get activeSuggestedPlan() {
    return (
      this._entities.billingStore.billingPlansStore.getItem(
        this.subscriptionSuggestion?.suggested_plan ||
          this.subscriptionSuggestion?.suggested_annual_plan ||
          ''
      ) || null
    )
  }

  private get suggestedPlan() {
    const subscriptionSuggestion = this.subscriptionSuggestion
    if (!subscriptionSuggestion) return null

    const suggestedPlanId =
      subscriptionSuggestion.suggested_annual_plan || subscriptionSuggestion.suggested_plan

    if (!suggestedPlanId) return null

    return this._entities.billingStore.billingPlansStore.getItem(suggestedPlanId) || null
  }

  get suggestedPlanCredits() {
    return this.activeSuggestedPlan?.credits || 0
  }

  get suggestedYearlyPlanCredits() {
    return this.activeYearlySuggestedPlan?.credits || 0
  }

  get suggestedPlanExtraCredits() {
    const subscriptionSuggestion = this.subscriptionSuggestion
    const activeSuggestedPlan = this.activeSuggestedPlan
    if (!subscriptionSuggestion || !activeSuggestedPlan) return 0

    const boughtCreditsAmount = subscriptionSuggestion.bought_credits_amount || 0
    const planCredits = this.subscriptionPlanCredits || 0
    const totalCreditsUsed = boughtCreditsAmount + planCredits

    return Math.max(0, totalCreditsUsed - activeSuggestedPlan.credits)
  }

  private calculatePlanExtraCredits = (
    plan: BillingPlan | null | undefined,
    creditsMultiplier = 1
  ): number => {
    if (!plan) return 0

    const totalCreditsUsed = this.baseCreditsUsed * creditsMultiplier

    return Math.max(0, totalCreditsUsed - plan.credits)
  }

  private calculatePlanExtraPrice = (
    plan: BillingPlan | null | undefined,
    creditsMultiplier = 1
  ): number => {
    if (!plan) return 0

    const extraCredits = this.calculatePlanExtraCredits(plan, creditsMultiplier)
    const additionalCreditAmount = plan.origin.additional_credit_amount || 0

    return Math.ceil(extraCredits * additionalCreditAmount)
  }

  get suggestedYearlyPlanExtraCredits() {
    if (!this.isCurrentPlanNotYearlyAndPeriodYearly) return 0

    return this.calculatePlanExtraCredits(this.activeYearlySuggestedPlan, 12)
  }

  get suggestedPlanExtraPrice() {
    const activeSuggestedPlan = this.activeSuggestedPlan
    if (!activeSuggestedPlan) return 0

    const extraCredits = this.suggestedPlanExtraCredits
    const additionalCreditAmount = activeSuggestedPlan.origin.additional_credit_amount || 0

    return Math.ceil(extraCredits * additionalCreditAmount)
  }

  get suggestedYearlyPlanExtraPrice() {
    if (!this.isCurrentPlanNotYearlyAndPeriodYearly) return 0

    return this.calculatePlanExtraPrice(this.activeYearlySuggestedPlan, 12)
  }

  get suggestedPlanTotalPrice() {
    return this.calculateSuggestedPlanTotalPrice(this.activeSuggestedPlan)
  }

  get suggestedPlanAdditionalCreditPrice() {
    return this.activeSuggestedPlan?.origin.additional_credit_amount || 0
  }

  get additionalCreditPriceDifference() {
    const isCustomPlan = this.subscriptionPlan?.custom || false
    let additionalCreditPrice = this.subscriptionPlan?.additional_credit_amount || 0

    if (isCustomPlan) {
      additionalCreditPrice = Math.round(additionalCreditPrice * 10000) / 100
    }

    const suggestedPlanAdditionalCreditPrice = this.suggestedPlanAdditionalCreditPrice

    if (additionalCreditPrice > 0 && suggestedPlanAdditionalCreditPrice < additionalCreditPrice) {
      return (
        ((additionalCreditPrice - suggestedPlanAdditionalCreditPrice) / additionalCreditPrice) * 100
      )
    }

    return 0
  }

  get upgradeSaveCost() {
    const subscriptionSuggestion = this.subscriptionSuggestion
    const suggestedPlan = this.suggestedPlan
    const isCustomPlan = this.subscriptionPlan?.custom || false

    if (isCustomPlan || !suggestedPlan) return 0

    const roundedPlanPrice = isCustomPlan
      ? Math.round(this.subscriptionPlan?.price || 0) / 100
      : Math.round(this.subscriptionPlan?.price || 0)
    const ceilOverageCost = Math.ceil(subscriptionSuggestion?.bought_credits_cost || 0)
    const currentPlanTotalPrice = Math.ceil(roundedPlanPrice + ceilOverageCost)

    if (this.isCurrentPlanNotYearlyAndPeriodYearly) {
      return currentPlanTotalPrice * 12 - this.suggestedYearlyPlanTotalPrice
    }

    return currentPlanTotalPrice - this.suggestedPlanTotalPrice
  }

  get userProfile() {
    return this._entities.usersStore.user
  }

  get organization() {
    return this._entities.organizationStore
  }

  handleKeepCurrentPlan = () => {
    this.sendOpportunityEvent('Keep current plan')
    modalStore.closeModal('opportunityModal')

    if (!this._entities.featureFlagsStore.billing_overview_v2) {
      uiStore.changeRouteViaVue({
        path: `/${BillingPath.overview}`,
      })
    }
  }

  chatWithSalesFromModalHandler = () => {
    openIntercom()
    modalStore.closeModal('opportunityModal')
    this.sendOpportunityEvent('Chat with Sales')
  }

  onBookMeetingViaHubSpotHandler = () => {
    const organizationExistingDays = this.organization.organizationExistingDays

    const isNewOrganization = organizationExistingDays < 90
    const hubspotUrl = isNewOrganization ? LINK_MEETINGS_HUBSPOT_1 : LINK_MEETINGS_HUBSPOT_2

    window.open(hubspotUrl, '_blank')
    this.sendOpportunityEvent('Book a Meeting')
  }

  sendOpportunityEvent = (action: string) => {
    if (!this.subscriptionSuggestion) return

    const eventParams: Record<string, unknown> = {
      event_id: 'flag_upgrade_opportunity',
      userId: this.userProfile?.id ?? undefined,
      context: { groupId: this.organization.id ?? undefined },
      plan_type: this.subscriptionPlan?.id || '',
      'MRR/ARR': this.upgradeSaveCost,
      action,
    }

    this._entities.eventLogStore.logEvent('Flag_Upgrade_Opportunity', eventParams, {
      groupId: this.organization.id ?? undefined,
    })
  }

  handleSeeOtherPlans = () => {
    this.sendOpportunityEvent('See other Plans')
    modalStore.closeModal('opportunityModal')
    uiStore.changeRoute({
      path: `/${BillingPath.upgrade}`,
    })
  }

  get planBasePrice(): number {
    if (this.showAdditionalPriceInfo) {
      return this.activeYearlySuggestedPlan?.price_dollars || 0
    }
    return this.activeSuggestedPlan?.price_dollars || 0
  }

  get totalCreditsUsed(): number {
    return this.currentPlanData.planCredits + this.currentPlanData.overageCredits
  }

  private readonly PERIOD_TO_BILLING_INTERVAL: Record<string, BillingInterval> = {
    annual: 'annual',
    yearly: 'annual',
    quarterly: 'quarterly',
    'semi-annual': 'semi-annual',
    monthly: 'monthly',
  }

  private mapPeriodToBillingInterval(period: string | null | undefined): BillingInterval {
    const key = (period || '').toLowerCase()
    return this.PERIOD_TO_BILLING_INTERVAL[key] || 'monthly'
  }

  get convertedCurrentPlanCredits(): ConvertedCurrentPlanCredits {
    const { planCredits, overageCredits, planPeriod, planPrice, overageCost } = this.currentPlanData
    const totalCredits = planCredits + overageCredits

    if (this.showAdditionalPriceInfo) {
      const currentPlanPeriodStr = this.subscriptionSuggestion?.current_plan_period
      const currentPlanPeriod = this.mapPeriodToBillingInterval(currentPlanPeriodStr)
      const suggestedPlanPeriod = this.suggestedPlanPeriod
      const currentPeriodMonths = Math.max(1, getIntervalMonthCount(currentPlanPeriod))
      const suggestedPeriodMonths = suggestedPlanPeriod === 'year' ? 12 : 1
      const conversionMultiplier = suggestedPeriodMonths / currentPeriodMonths

      return {
        totalCredits: totalCredits * conversionMultiplier,
        planCredits: planCredits * conversionMultiplier,
        planPeriod: 'year',
        planPrice: planPrice * conversionMultiplier,
        overageCredits: overageCredits * conversionMultiplier,
        overageCost: overageCost * conversionMultiplier,
      }
    }

    return { totalCredits, planCredits, planPeriod, planPrice, overageCredits, overageCost }
  }

  get phoneInputStore() {
    return this._phoneInputStore
  }

  get selectedDate() {
    return this._selectedDate
  }

  setSelectedDate = (newDate: Date | null) => {
    this._selectedDate = newDate
  }

  get isLoading() {
    return this._loadingGetCall
  }

  handleBack = () => {
    this.closeGetCallModal()
  }

  handleSave = async () => {
    if (!this._phoneInputStore.isValidNumber || !this._phoneInputStore.number.trim()) {
      this._phoneInputStore.triggerNumberValidation()
      return
    }

    if (!this._selectedDate) {
      showToast({
        type: 'error',
        title: 'Please select a date and time',
      })
      return
    }

    const fieldMapping: Record<string, string> = {
      phone: '2-20572722/phone',
      firstname: 'firstname',
      lastname: 'lastname',
      email: 'email',
      organization_id: 'organization_id',
    }

    const fields = [
      {
        name: fieldMapping.phone,
        value: this._phoneInputStore.fullNumber,
      },
      {
        name: fieldMapping.firstname,
        value: this.userProfile?.first_name || '',
      },
      {
        name: fieldMapping.lastname,
        value: this.userProfile?.last_name || '',
      },
      {
        name: fieldMapping.email,
        value: this.userProfile?.email || '',
      },
      {
        name: fieldMapping.organization_id,
        value: String(this.organization.id || ''),
      },
    ]

    runInAction(() => {
      this._loadingGetCall = true
    })

    this.sendOpportunityEvent('Get a call')

    await submitDataToHubspotForm({
      portalId: getEnvVar('HUBSPOT_PORTAL_ID_GET_A_CALL'),
      formId: getEnvVar('HUBSPOT_FORM_ID_GET_A_CALL'),
      fields: fields,
      successCallback: () => {
        showToast({
          type: 'success',
          title: 'Call request sent',
          desc: 'We’ll call you at the scheduled time',
        })
        this.closeGetCallModal()
      },
      finallyCallback: () => {
        runInAction(() => {
          this._loadingGetCall = false
        })
      },
      isShowErrorMessage: true,
    })
  }
}
